import { useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Calendar } from "lucide-react";
import { cn } from "@/lib/utils";
import { ListPagination } from "@/components/ui/list-pagination";
import { PlanTimelineItem } from "@/components/plan-timeline-item";
import { formatDate } from "@/modules/calculator";

interface Show {
  id: number;
  date: string;
  venue: {
    id: number;
    name: string;
    city?: string | null;
    state?: string | null;
  };
  artist?: {
    id: number;
    name: string;
  } | null;
}

interface ShowPackage {
  showId: number;
  setCore: boolean;
  setAudio: boolean;
  setVideo: boolean;
}

interface PlanCalendarProps {
  shows: Show[];
  showPackages: Record<number, ShowPackage>;
  campaignStart?: string;
  campaignEnd?: string;
}

interface TimelineEvent {
  date: Date;
  shows: Show[];
  position: number; // 0-100 percentage for positioning
}

export function PlanCalendar({ shows, showPackages, campaignStart, campaignEnd }: PlanCalendarProps) {
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState<number | 'all'>(10);

  // Sort shows by date and group by same date
  const timelineEvents = useMemo(() => {
    if (shows.length === 0) return [];

    // Sort shows by date
    const sortedShows = [...shows].sort((a, b) =>
      new Date(a.date).getTime() - new Date(b.date).getTime()
    );

    // Group shows by date (using UTC to avoid timezone issues)
    const groupedByDate = sortedShows.reduce((acc, show) => {
      // Use the ISO date string directly as the key (YYYY-MM-DD format)
      const dateKey = show.date.split('T')[0];
      if (!acc[dateKey]) {
        acc[dateKey] = [];
      }
      acc[dateKey].push(show);
      return acc;
    }, {} as Record<string, Show[]>);

    // Get timeline range
    const firstShowDate = new Date(sortedShows[0].date);
    const lastShowDate = new Date(sortedShows[sortedShows.length - 1].date);

    const timelineStart = campaignStart
      ? new Date(Math.min(new Date(campaignStart).getTime(), firstShowDate.getTime()))
      : firstShowDate;

    const timelineEnd = campaignEnd
      ? new Date(Math.max(new Date(campaignEnd).getTime(), lastShowDate.getTime()))
      : lastShowDate;

    const totalDuration = timelineEnd.getTime() - timelineStart.getTime();

    // Create timeline events with positions
    // Map positions to 10-90% range to keep dots within bounds
    return Object.entries(groupedByDate).map(([dateKey, dateShows]) => {
      // Parse ISO date string with UTC to avoid timezone issues
      const date = new Date(dateKey + 'T00:00:00Z');
      const rawPosition = totalDuration > 0
        ? (date.getTime() - timelineStart.getTime()) / totalDuration
        : 0.5;

      // Remap from 0-1 to 10-90% range
      const position = 10 + (rawPosition * 80);

      return {
        date,
        shows: dateShows,
        position,
      };
    }).sort((a, b) => a.date.getTime() - b.date.getTime());
  }, [shows, campaignStart, campaignEnd]);

  // Get package color for timeline dots
  const getPackageColor = (showId: number): string => {
    const pkg = showPackages[showId];
    if (!pkg) return "bg-gray-400";

    const activePackages = [pkg.setCore, pkg.setAudio, pkg.setVideo].filter(Boolean).length;

    if (activePackages > 1) return "bg-green-500";
    if (pkg.setVideo) return "bg-purple-500";
    if (pkg.setAudio) return "bg-blue-500";
    if (pkg.setCore) return "bg-gray-500";

    return "bg-gray-400";
  };

  // Calculate campaign duration
  const campaignDuration = useMemo(() => {
    if (!campaignStart || !campaignEnd) return null;

    const start = new Date(campaignStart);
    const end = new Date(campaignEnd);
    const days = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));

    return { start, end, days };
  }, [campaignStart, campaignEnd]);

  // Calculate intermediate date markers
  const dateMarkers = useMemo(() => {
    if (shows.length === 0) return [];

    const sortedShows = [...shows].sort((a, b) =>
      new Date(a.date).getTime() - new Date(b.date).getTime()
    );

    const firstDate = new Date(sortedShows[0].date);
    const lastDate = new Date(sortedShows[sortedShows.length - 1].date);
    const totalDays = Math.ceil((lastDate.getTime() - firstDate.getTime()) / (1000 * 60 * 60 * 24));

    const markers: Array<{ date: Date; position: number; label: string }> = [];

    // Determine marker interval based on duration
    let interval: number;
    let format: Intl.DateTimeFormatOptions;

    if (totalDays <= 14) {
      // < 2 weeks: show every 3 days
      interval = 3 * 24 * 60 * 60 * 1000;
      format = { month: 'short', day: 'numeric' };
    } else if (totalDays <= 60) {
      // 2 weeks - 2 months: show weekly
      interval = 7 * 24 * 60 * 60 * 1000;
      format = { month: 'short', day: 'numeric' };
    } else {
      // > 2 months: show monthly
      interval = 30 * 24 * 60 * 60 * 1000;
      format = { month: 'short', year: 'numeric' };
    }

    // Generate markers
    let currentDate = new Date(firstDate);
    const endTime = lastDate.getTime();
    const minDistanceFromEnds = interval * 0.3; // Minimum distance from start/end dates

    while (currentDate.getTime() < endTime) {
      currentDate = new Date(currentDate.getTime() + interval);

      // Only add marker if it's not too close to the end date
      if (currentDate.getTime() <= endTime) {
        const distanceFromEnd = endTime - currentDate.getTime();

        // Skip if too close to the end
        if (distanceFromEnd < minDistanceFromEnds) {
          continue;
        }

        const rawPosition = (currentDate.getTime() - firstDate.getTime()) / (endTime - firstDate.getTime());
        const position = 10 + (rawPosition * 80);

        markers.push({
          date: new Date(currentDate),
          position,
          label: currentDate.toLocaleDateString('en-US', { ...format, timeZone: 'UTC' })
        });
      }
    }

    return markers;
  }, [shows]);

  // Paginated timeline events
  const paginatedEvents = useMemo(() => {
    if (itemsPerPage === 'all') {
      return timelineEvents;
    }

    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return timelineEvents.slice(startIndex, endIndex);
  }, [timelineEvents, currentPage, itemsPerPage]);

  // Calculate total pages
  const totalPages = itemsPerPage === 'all'
    ? 1
    : Math.ceil(timelineEvents.length / itemsPerPage);

  // Reset to page 1 when items per page changes
  const handleItemsPerPageChange = (newItemsPerPage: number | 'all') => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  if (shows.length === 0) {
    return (
      <Card className="border-pink-200/50 overflow-hidden flex flex-col h-full shadow-lg">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            Show Timeline
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center justify-center h-[200px] text-muted-foreground">
            <Calendar className="h-12 w-12 mb-2 opacity-20" />
            <p>No shows selected</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Use sorted timeline events for accurate start/end dates
  const firstShow = timelineEvents.length > 0 ? timelineEvents[0].date : new Date(shows[0].date);
  const lastShow = timelineEvents.length > 0 ? timelineEvents[timelineEvents.length - 1].date : new Date(shows[shows.length - 1].date);
  const showDuration = Math.ceil((lastShow.getTime() - firstShow.getTime()) / (1000 * 60 * 60 * 24));

  return (
    <Card className="plan-calendar border-pink-200/50 overflow-hidden flex flex-col h-full shadow-lg">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 justify-between">
          <div className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            Show Timeline
            <span className="text-sm font-normal text-muted-foreground">
              ({shows.length} {shows.length === 1 ? 'show' : 'shows'})
            </span>
          </div>
          {campaignDuration && (
            <div className="text-sm font-normal text-muted-foreground">
              Campaign: {campaignDuration.days} days
              <span className="text-xs ml-2">
                ({formatDate(campaignStart!)} - {formatDate(campaignEnd!)})
              </span>
            </div>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {/* Timeline visualization */}
        <div className="relative h-40 mb-6">
          {/* Timeline bar with gradient and shadow */}
          <div className="absolute top-16 left-0 right-0 h-1.5 bg-gradient-to-r from-pink-500 via-purple-500 to-blue-500 rounded-full shadow-md"></div>

          {/* Timeline padding indicators (visual guides) */}
          <div className="absolute top-16 left-0 w-[10%] h-1.5 bg-gradient-to-r from-transparent to-pink-400/50 rounded-l-full"></div>
          <div className="absolute top-16 right-0 w-[10%] h-1.5 bg-gradient-to-l from-transparent to-blue-400/50 rounded-r-full"></div>

          {/* Start and end markers - positioned at 10% and 90% */}
          <div className="absolute top-16 flex flex-col items-center" style={{ left: '10%', transform: 'translateX(-50%)' }}>
            <div className="w-0.5 h-4 bg-gradient-to-b from-pink-500 to-pink-400 mt-0.5"></div>
            <div className="text-xs text-pink-600 whitespace-nowrap font-semibold mt-1.5 text-center">
              {formatDate(timelineEvents[0].shows[0].date)}
            </div>
          </div>

          <div className="absolute top-16 flex flex-col items-center" style={{ left: '90%', transform: 'translateX(-50%)' }}>
            <div className="w-0.5 h-4 bg-gradient-to-b from-blue-500 to-blue-400 mt-0.5"></div>
            <div className="text-xs text-blue-600 whitespace-nowrap font-semibold mt-1.5 text-center">
              {formatDate(timelineEvents[timelineEvents.length - 1].shows[0].date)}
            </div>
          </div>

          {/* Intermediate date markers */}
          {dateMarkers.map((marker, idx) => (
            <div
              key={idx}
              className="absolute top-16 flex flex-col items-center"
              style={{ left: `${marker.position}%`, transform: 'translateX(-50%)' }}
            >
              {/* Tick mark */}
              <div className="w-px h-3 bg-gradient-to-b from-purple-400 to-purple-300 mt-0.5"></div>
              {/* Date label */}
              <div className="text-[10px] text-purple-500 whitespace-nowrap mt-1 font-medium text-center">
                {marker.label}
              </div>
            </div>
          ))}

          {/* Show markers */}
          {timelineEvents.map((event, idx) => {
            // Calculate proper vertical position to center dot on timeline bar
            // Timeline bar is at top-16 (64px) with h-1 (4px), center at ~66px
            // Small dot (w-5 h-5 = 20px) needs top at 66-10 = 56px
            // Large dot (w-6 h-6 = 24px) needs top at 66-12 = 54px
            const isMultiShow = event.shows.length > 1;
            const dotTop = isMultiShow ? '54px' : '56px';

            return (
              <div
                key={idx}
                className="absolute group cursor-pointer"
                style={{
                  left: `${event.position}%`,
                  top: dotTop,
                  transform: 'translateX(-50%)',
                }}
              >
                {/* Marker dot or numbered circle */}
                <div className="relative flex flex-col items-center">
                  {isMultiShow ? (
                    // Multiple shows - show numbered circle
                    <div className="w-6 h-6 bg-white rounded-full flex items-center justify-center text-[11px] font-bold border-2 border-gray-400 shadow-lg transition-all group-hover:scale-125 group-hover:shadow-xl">
                      {event.shows.length}
                    </div>
                  ) : (
                    // Single show - show colored dot
                    <div
                      className={cn(
                        "rounded-full border-2 border-white shadow-lg transition-all group-hover:scale-125 group-hover:shadow-xl w-5 h-5",
                        getPackageColor(event.shows[0].id)
                      )}
                    />
                  )}

                  {/* Tooltip on hover */}
                  <div className="absolute top-8 left-1/2 transform -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity z-20 pointer-events-none">
                    <div className="bg-gray-900 text-white text-xs rounded-lg py-2 px-3 shadow-xl whitespace-nowrap">
                      <div className="font-semibold mb-1.5 pb-1 border-b border-gray-700">
                        {formatDate(event.shows[0].date)}
                      </div>
                      {event.shows.map((show, showIdx) => (
                        <div key={show.id} className={cn("text-[11px]", showIdx > 0 && "mt-1.5 pt-1.5 border-t border-gray-700")}>
                          <div className="font-medium text-white">{show.artist?.name || "Unknown Artist"}</div>
                          <div className="text-gray-400">{show.venue.city}, {show.venue.state}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Show list - paginated on screen, all items in print */}
        <div className="space-y-2">
          {/* Screen view - paginated */}
          <div className="print:hidden space-y-2">
            {paginatedEvents.map((event, idx) => (
              <div key={idx} className="space-y-2">
                {event.shows.map((show, showIdx) => (
                  <PlanTimelineItem
                    key={show.id}
                    show={show}
                    showPackages={showPackages}
                    showCount={showIdx === 0 && event.shows.length > 1 ? event.shows.length : undefined}
                  />
                ))}
              </div>
            ))}
          </div>

          {/* Print view - all items */}
          <div className="hidden print:block space-y-2">
            {timelineEvents.map((event, idx) => (
              <div key={idx} className="space-y-2">
                {event.shows.map((show, showIdx) => (
                  <PlanTimelineItem
                    key={show.id}
                    show={show}
                    showPackages={showPackages}
                    showCount={showIdx === 0 && event.shows.length > 1 ? event.shows.length : undefined}
                  />
                ))}
              </div>
            ))}
          </div>

          {/* Pagination - only on screen */}
          <ListPagination
            currentPage={currentPage}
            totalPages={totalPages}
            itemsPerPage={itemsPerPage}
            totalItems={timelineEvents.length}
            onPageChange={setCurrentPage}
            onItemsPerPageChange={handleItemsPerPageChange}
          />
        </div>

        {/* Duration summary */}
        <div className="mt-4 pt-4 border-t border-gray-200 flex items-center justify-between text-sm text-muted-foreground">
          <div>
            <span className="font-medium">{timelineEvents.length}</span> event {timelineEvents.length === 1 ? 'date' : 'dates'}
          </div>
          <div>
            <span className="font-medium">{showDuration}</span> day{showDuration === 1 ? '' : 's'} span
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
